import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GitHubIcon from "@mui/icons-material/GitHub";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ProjectDetailsDialog from "./ProjectDetailsDialog";
import { ProjectCardProps } from "../@types/types";

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Card
      raised
      sx={{
        height: "100%",
        position: "relative",
        overflow: "hidden",
        "&:hover .overlay": {
          opacity: [0, 1],
          visibility: "visible",
        },
        "&:hover .live-preview-button": {
          opacity: [0, 1],
          transform: "translateY(0)",
        },
      }}
    >
      <CardMedia
        component="img"
        height="280"
        image={project.imageUrl[0]}
        alt={`${project.name} image`}
        sx={{
          width: "100%",
          height: "auto",
          borderRadius: "0px 0px 30px 30px",
        }}
      />

      <CardContent sx={{ flexGrow: 1 }}>
        <Typography
          gutterBottom
          variant="h5"
          component="h3"
          sx={{ textTransform: "uppercase" }}
        >
          {project.name}
        </Typography>
        <Typography variant="body2" color="textSecondary" component="p">
          {project.description}
        </Typography>
      </CardContent>

      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            padding: "10px",
          }}
        >
          <Tooltip title={t("Source code")}>
            <Button href={project.githubUrl} target="_blank" color="inherit">
              <GitHubIcon />
            </Button>
          </Tooltip>
          <Tooltip title={t("Live preview")}>
            <Button href={project.projectLink} target="_blank" color="inherit">
              <VisibilityIcon />
            </Button>
          </Tooltip>
        </Box>
      )}

      {!isMobile && (
        <Box
          className="overlay"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            flexDirection: "row",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            opacity: 0,
            visibility: "hidden",
            transition: "opacity 0.3s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            backdropFilter: "blur(4px)",
          }}
        >
          <Tooltip title={t("Source code")}>
            <Button
              variant="contained"
              href={project.githubUrl}
              target="_blank"
              color="inherit"
            >
              <GitHubIcon />
            </Button>
          </Tooltip>
          <Tooltip title={t("Live preview")}>
            <Button
              variant="contained"
              href={project.projectLink}
              target="_blank"
              color="inherit"
            >
              <VisibilityIcon />
            </Button>
          </Tooltip>
          <Tooltip title={t("Learn more")}>
            <Button variant="contained" onClick={handleOpen} color="primary">
              <MoreHorizIcon />
            </Button>
          </Tooltip>
        </Box>
      )}

      <ProjectDetailsDialog
        open={open}
        handleClose={handleClose}
        project={project}
      />
    </Card>
  );
};

export default ProjectCard;
